import styled, { css } from '@app/styled';
import { color, modifier, radius, spacing, typography, variants } from '@app/theme';

export const NavItem = styled.button.attrs(() => ({ type: 'button' }))<{ $active?: boolean }>`
    border: 0;
    padding: 0;
    background: none;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    height: 65px;
    color: ${color('custom.content.secondary')};

    &:hover {
        color: ${color('custom.interactive.primary.idle')};
        background-color: ${color('custom.surface.secondary')};
    }

    ${modifier(
        '$active',
        css`
            color: ${color('custom.interactive.primary.idle')};
            background-color: ${color('custom.surface.secondary')};
        `,
    )};
`;

export const Badge = styled.div`
    position: absolute;
    top: calc(50% - 16px);
    left: calc(50% + 2px);

    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;

    width: 16px;
    height: 16px;
    border-radius: ${radius('rounded')};
    background-color: ${color('custom.red.500')};

    font-size: 10px;
    text-transform: uppercase;
    color: ${color('custom.content.white')};
`;

export const TasksContainer = styled.section`
    width: 487px;
    max-height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
`;

export const EmptyContainer = styled.div`
    text-align: center;
    padding: ${spacing(8)};
    width: 360px;
`;

export const LoadingContainer = styled.div`
    height: 100px;
    width: 487px;
`;

export const NoTasksHeading = styled.h3`
    ${typography('custom.heading3')};
`;

export const NoTasksText = styled.div`
    ${typography('custom.body')};
    color: ${color('custom.content.secondary')};
`;

export const TaskContainer = styled.article`
    padding: ${spacing(4)};

    &:not(:first-child)::before {
        display: block;
        content: '';
        height: 1px;
        background: ${color('custom.border.primary')};
        margin: ${spacing(1)} -${spacing(4)};
        // Since "::before" is the first child of the element, it is within the padding. So we need to move the
        // divider up by the amount of padding.
        transform: translateY(-${spacing(4)});
    }
`;

export const Title = styled.h3`
    ${typography('custom.title4')};
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const Description = styled.div`
    ${typography('custom.description')};
    color: ${color('custom.content.secondary')};
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const LanguagesTable = styled.table`
    table-layout: fixed;
    margin-top: ${spacing(3)};
    width: calc(100% + 32px); // 100% - padding
    margin-left: calc(${spacing(4)} * -1); // -16px
    border-collapse: separate;

    thead th {
        padding-bottom: ${spacing(1)};
    }

    tbody td {
        padding-top: ${spacing(1)};
        padding-bottom: ${spacing(1)};
    }

    thead th:first-child,
    tbody td:first-child {
        padding-left: ${spacing(4)};
    }

    thead th,
    tbody td {
        padding-left: ${spacing(2)};
    }

    thead th:last-child,
    tbody td:last-child {
        padding-right: ${spacing(4)};
    }
`;

type AlignmentProps = { $align?: 'right' | 'left' };
const withAlignment = variants(
    '$align',
    {
        left: css`
            text-align: left;
        `,
        right: css`
            text-align: right;
        `,
    },
    'left',
);

export const TableHeading = styled.th<AlignmentProps>`
    ${typography('custom.label')};
    color: ${color('custom.content.secondary')};

    ${withAlignment}

    &:nth-child(1) {
        width: 184px; // 160px base + 8px padding + 16px padding
    }

    &:nth-child(2) {
        width: 98px; // 90px base + 8px padding
    }

    &:nth-child(3) {
        width: 62px; // 54px base + 8px padding
    }

    &:nth-child(4) {
        width: 118px; // 94px base + 8 padding + 16px padding
    }
`;

export const Cell = styled.td<AlignmentProps & { $secondary?: boolean }>`
    ${typography('custom.description')};
    overflow: hidden;
    text-overflow: ellipsis;

    ${withAlignment};

    ${modifier(
        '$secondary',
        css`
            color: ${color('custom.content.secondary')};
        `,
    )};
`;

export const Row = styled.tr<{ $completed?: boolean }>`
    ${modifier(
        '$completed',
        css`
            background-color: ${color('custom.surface.primary50')};
            td:first-child {
                border-left: 2px solid ${color('custom.interactive.primary.idle')};
            }
        `,
    )};
`;

export const Status = styled.span<{ $status: 'completed' | 'in_progress' | 'created' | 'queued' }>`
    display: flex;
    align-items: center;
    justify-content: left;
    color: ${color('custom.content.secondary')};

    &::before {
        display: inline-block;
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        // This is specific value to give overall size of 16px.
        // And there's some weird rendering glitch that causes the icon not to be rendered in center properly
        // so we add a bit at the top more to center visually
        margin: 6px 5px 5px;
    }

    ${variants('$status', {
        completed: css`
            &::before {
                background-color: ${color('custom.green.400')};
            }
        `,
        in_progress: css`
            &::before {
                background-color: ${color('custom.yellow.400')};
            }
        `,
        created: css`
            &::before {
                background-color: ${color('custom.border.primary')};
            }
        `,
        queued: css`
            &::before {
                background-color: ${color('custom.border.primary')};
            }
        `,
    })};
`;

export const Metadata = styled.p`
    ${typography('custom.message')};
    color: ${color('custom.content.secondary')};
    margin: ${spacing(3)} 0 0;
`;

export const MetaDataItem = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    flex-basis: 100%;
    max-width: max-content;

    &:not(:first-child) {
        margin-left: ${spacing(1)};
    }

    &:not(:first-child)::before {
        content: '•';
        margin-right: ${spacing(1)};
    }
`;

export const StrongInlineContent = styled.strong`
    display: contents;
`;
